import { useEffect, useState } from 'react'
import { fxExpensesApi, fxSuppliersApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface Expense {
  id: number
  category: string
  description: string
  amount: number
  expense_date: string
  payment_method: string | null
  supplier_id: number | null
  supplier_name: string | null
  notes: string | null
  is_active: boolean
}

interface Supplier {
  id: number
  name: string
  is_active: boolean
}

interface ExpenseForm {
  category: string
  description: string
  amount: string
  expense_date: string
  payment_method: string
  supplier_id: string
  notes: string
}

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'alquiler', label: 'Alquiler' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'transporte', label: 'Transporte' },
  { value: 'planilla', label: 'Planilla' },
  { value: 'impuestos', label: 'Impuestos' },
  { value: 'otro', label: 'Otro' },
]

export const FX_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((c) => [c.value, c.label])
)

const PAYMENT_METHOD_OPTIONS = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'tarjeta', label: 'Tarjeta' },
]

const todayIso = () => new Date().toISOString().split('T')[0]

const emptyForm = (): ExpenseForm => ({
  category: CATEGORY_OPTIONS[0].value,
  description: '',
  amount: '',
  expense_date: todayIso(),
  payment_method: '',
  supplier_id: '',
  notes: '',
})

export default function FxExpensesTab() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<ExpenseForm>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchAll = async () => {
    try {
      const [expensesRes, suppliersRes] = await Promise.all([fxExpensesApi.list(), fxSuppliersApi.list()])
      setExpenses(expensesRes.data.expenses || [])
      setSuppliers(suppliersRes.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching FX expenses:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const { sortedData, sortConfig, requestSort } = useSortableData(expenses, {
    key: 'expense_date',
    direction: 'desc',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm())
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (exp: Expense) => {
    setForm({
      category: exp.category,
      description: exp.description,
      amount: String(exp.amount),
      expense_date: exp.expense_date,
      payment_method: exp.payment_method || '',
      supplier_id: exp.supplier_id ? String(exp.supplier_id) : '',
      notes: exp.notes || '',
    })
    setEditingId(exp.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.description.trim()) {
      setError('La descripción es requerida')
      return
    }
    const amountNum = Number(form.amount)
    if (!form.amount || Number.isNaN(amountNum) || amountNum <= 0) {
      setError('El monto debe ser un número mayor a 0')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        category: form.category,
        description: form.description.trim(),
        amount: amountNum,
        expense_date: form.expense_date,
        payment_method: form.payment_method || null,
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        notes: form.notes.trim() || null,
      }
      if (editingId) {
        await fxExpensesApi.update(editingId, payload)
      } else {
        await fxExpensesApi.create(payload)
      }
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el gasto')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (exp: Expense) => {
    try {
      await fxExpensesApi.update(exp.id, { is_active: !exp.is_active })
      fetchAll()
    } catch (err) {
      console.error('Error updating FX expense:', err)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Gastos operativos de FX Admin. Un gasto registrado por error no se elimina, se anula.
        </p>
        <button
          onClick={openNew}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Nuevo Gasto
        </button>
      </div>

      {expenses.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay gastos registrados aún.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Fecha" sortKey="expense_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Categoría" sortKey="category" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Descripción" sortKey="description" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Monto" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedData.map((exp) => (
                  <tr key={exp.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{exp.expense_date}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {FX_CATEGORY_LABELS[exp.category] || exp.category}
                    </td>
                    <td className="px-4 py-4 text-sm text-espresso-500 max-w-xs truncate" title={exp.description}>
                      {exp.description}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">
                      S/ {Number(exp.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${exp.is_active ? 'bg-green-100 text-green-800' : 'bg-cream-200 text-espresso-700'}`}>
                        {exp.is_active ? 'Activo' : 'Anulado'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(exp)} variant="primary">Editar</TableActionButton>
                        <TableActionButton onClick={() => handleToggleActive(exp)} variant="danger">
                          {exp.is_active ? 'Anular' : 'Reactivar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Gasto' : 'Nuevo Gasto'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Categoría *</label>
                  <select name="category" value={form.category} onChange={handleChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                    {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Monto *</label>
                  <input type="number" step="0.01" min="0.01" name="amount" value={form.amount} onChange={handleChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Descripción *</label>
                <input type="text" name="description" value={form.description} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Fecha *</label>
                  <input type="date" name="expense_date" value={form.expense_date} onChange={handleChange} required
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Método de pago</label>
                  <select name="payment_method" value={form.payment_method} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                    <option value="">Sin especificar</option>
                    {PAYMENT_METHOD_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Proveedor</label>
                <select name="supplier_id" value={form.supplier_id} onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500">
                  <option value="">Sin proveedor</option>
                  {suppliers.filter((s) => s.is_active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500" />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
